import React, { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useEffectOnce } from 'react-use';
import { setIsPlaidInfoMessageVisibility } from '../../Plaid/state/actions';
import { ProfilePageHeader } from '../../ProfileOPL/components/ProfilePageHeader/ProfilePageHeader';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { ACCOUNTS_QUERY_KEY } from './AccountsConstants';
import { TabMenu } from './components/TabMenu/TabMenu';
import { Wrapper } from './components/Wrapper/Wrapper';
import { AccountsProvider } from './context/AccountsProvider';
import { AccountPreferences } from './Features/AccountPreferences/AccountPreferences';
import { ManageLinks } from './Features/ManageLinks/ManageLinks';
import { TabSlug, TabSlugOptions, WorkflowTabSlugOptions } from './types/tab';

interface Props {
  clientId: string;
  tabSlug?: TabSlug;
  entityId?: string;
  workflowProps?: WorkflowComponentProps;
}

export const Accounts: React.FC<Props> = props => {
  const { clientId, tabSlug, workflowProps } = props;

  const isWorkflow = !!workflowProps;

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const { params } = ADV.util.Router.getObjFromHash(ADV.util.getHash());

  useEffectOnce(() => {
    return () => {
      dispatch(setIsPlaidInfoMessageVisibility(false));
    };
  });

  useEffect(() => {
    if (!isWorkflow) {
      if (!tabSlug || !(tabSlug in TabSlugOptions)) {
        ADV.util.Router.go2('ProfileAccounts', { clientId, tabSlug: TabSlugOptions.preferences });
      }
    }
  }, [clientId, isWorkflow, tabSlug]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries([ACCOUNTS_QUERY_KEY]);
    };
  }, [queryClient]);

  if (!tabSlug && !isWorkflow) {
    return null;
  }

  const currentPage = (tabSlug ||
    (params.pageId in TabSlugOptions && WorkflowTabSlugOptions[params.pageId]) ||
    TabSlugOptions.preferences) as TabSlug;

  return (
    <>
      <AccountsProvider
        value={{
          clientId,
          tabSlug: currentPage,
          isWorkflow,
          workflowId: workflowProps?.workflowId,
        }}
      >
        <Wrapper isWorkflow={isWorkflow}>
          <ProfilePageHeader
            title="Accounts"
            description="Please include bank accounts, investment accounts, retirement accounts, loans and debt."
          >
            <TabMenu />
          </ProfilePageHeader>

          {currentPage === TabSlugOptions.preferences ? (
            <AccountPreferences {...props} />
          ) : (
            <ManageLinks {...props} />
          )}
        </Wrapper>
      </AccountsProvider>

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={workflowProps.onNext}
        />
      )}
    </>
  );
};
